import { ChevronDown, CircleAlert, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router";
import type { CategoryDto, ProductDto } from "../../../shared/api-types";
import { ALLERGENS, type Currency, PRODUCT_LABELS } from "../../../shared/constants";
import { reviewText } from "../../../shared/import-merge";
import type { SnapshotProduct } from "../../../shared/snapshot";
import { ApiRequestError } from "../../api/client";
import { mediaUrl, menuApi } from "../../api/queries";
import { Drawer, Modal, StatusChip, Switch } from "../../components/kit";
import { useToast } from "../../components/Toast";
import { Alert, ICON } from "../../components/ui";
import { currencySymbol, currencyWord, plural, price } from "../../lib/format";
import { PHOTO_ACCEPT, photoFileError, preparePhoto } from "../../lib/photo";
import {
  type EditorForm,
  emptyForm,
  formFromProduct,
  isDirty,
  toProductInput,
  translationChip,
  validateForm,
  withFoodChange,
  withSourceChange,
} from "./editorForm";

export interface ProductEditorProps {
  rid: string;
  menuId: string;
  menuName: string;
  currency: Currency;
  categories: CategoryDto[];
  product?: ProductDto | undefined;
  /** The published version of this product, if any (for "Preț publicat acum"). */
  published?: SnapshotProduct | undefined;
  initialCategoryId?: string | undefined;
  /** "Adaugă produs" without a category chosen in the list: the editor asks for it. */
  askCategory?: boolean;
  onClose: () => void;
  onSaved: (saved: ProductDto, previous: ProductDto | undefined) => void;
  onAvailabilityChanged?: () => void;
}

/** Product editor drawer (prototype `ed`). Saves to the draft only; publishing is separate. */
export function ProductEditor(p: ProductEditorProps) {
  const toast = useToast();
  const api = menuApi(p.rid);
  const [initial] = useState<EditorForm>(() =>
    p.product ? formFromProduct(p.product) : emptyForm(p.initialCategoryId ?? p.categories[0]?.id ?? ""),
  );
  const [form, setForm] = useState<EditorForm>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [available, setAvailable] = useState(p.product?.available ?? true);
  const [open, setOpen] = useState({
    food: !p.product?.allergensConfirmed,
    labels: false,
    tr: false,
  });
  const body = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const errors = submitted ? { ...validateForm(form), ...serverErrors } : {};
  const errorList = [...new Set(Object.values(errors))];
  const set = (patch: Partial<EditorForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    setServerErrors({});
  };
  const sym = currencySymbol(p.currency);
  const trChip = translationChip(form, p.product);

  function requestClose() {
    if (isDirty(form, initial)) setConfirmClose(true);
    else p.onClose();
  }

  async function save() {
    const errs = validateForm(form);
    setSubmitted(true);
    setMessage("");
    if (Object.keys(errs).length) {
      if (errs["pricing.price"] === undefined && Object.keys(errs).some((k) => k.startsWith("allergens"))) {
        setOpen((o) => ({ ...o, food: true }));
      }
      setTimeout(() => body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 30);
      return;
    }
    setBusy(true);
    try {
      const input = toProductInput(form);
      const saved = p.product
        ? await api.updateProduct(p.menuId, p.product.id, input)
        : await api.createProduct(p.menuId, input);
      p.onSaved(saved, p.product);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiRequestError && err.code === "validation") {
        setServerErrors(err.fields);
        setTimeout(() => body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 30);
      } else setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  /** Prototype `onImg`: checked, downsized and uploaded when picked; saving the product links it to the draft. */
  async function pickPhoto(file: File) {
    const invalid = photoFileError(file);
    if (invalid) {
      setPhotoError(invalid);
      return;
    }
    setPhotoBusy(true);
    setPhotoError("");
    try {
      const media = await api.uploadPhoto(await preparePhoto(file));
      set({ imageId: media.id });
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : String(e));
    } finally {
      setPhotoBusy(false);
    }
  }

  async function toggleAvailability() {
    if (!p.product) return;
    const next = !available;
    setAvailable(next);
    try {
      const r = await api.setAvailability(p.product.id, next);
      p.onAvailabilityChanged?.();
      toast(
        `${form.name || p.product.name}: ${next ? "disponibil din nou" : "indisponibil azi"}. Apare imediat în meniul public.`,
        async () => {
          await api.setAvailability(r.productId, r.previous);
          setAvailable(r.previous);
          p.onAvailabilityChanged?.();
        },
      );
    } catch (err) {
      setAvailable(!next);
      toast(err instanceof Error ? err.message : String(err));
    }
  }

  const inv = (k: string) => (errors[k] ? "true" : "false");
  const err = (id: string, k: string) =>
    errors[k] ? (
      <span id={id} className="field-error">
        <CircleAlert size={16} strokeWidth={1.75} aria-hidden="true" />
        <span>{errors[k]}</span>
      </span>
    ) : null;
  const pubPriceHint =
    p.published && !p.published.variants
      ? `Preț publicat acum: ${price(p.published.priceMinor, p.currency)}`
      : p.product
        ? "Produsul nu a fost publicat încă."
        : `Moneda: ${p.currency === "EUR" ? "euro (EUR)" : "lei (RON)"}, din setările restaurantului.`;

  const foodSub = form.allergensConfirmed
    ? form.allergens.length
      ? plural(form.allergens.length, "alergen confirmat", "alergeni confirmați")
      : "Confirmat: fără alergeni din lista UE"
    : "Neconfirmat · clienții văd alergenii estimați de Meniova până îi confirmi";
  const labelsSub = form.labels.length
    ? form.labels.map((l) => PRODUCT_LABELS.find((x) => x[0] === l)?.[1]).join(", ")
    : "Opțional: vegetarian, vegan, picant, nou, recomandat";

  return (
    <Drawer
      title={p.product ? "Editează produsul" : "Produs nou"}
      sub={`${p.menuName} · se salvează în ciornă`}
      onClose={requestClose}
      footer={
        <>
          <span className="foot-note">Se salvează în ciornă. Publici separat.</span>
          <button type="button" className="btn btn-secondary" onClick={requestClose}>
            Renunță
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || photoBusy} onClick={save}>
            {busy ? "Se salvează…" : p.product ? "Salvează în ciornă" : "Adaugă în ciornă"}
          </button>
        </>
      }
    >
      <div ref={body} className="stack" style={{ gap: 20 }}>
        {p.product && p.product.reviewFlags.length > 0 && (
          <Alert tone="warning" title="De verificat după import">
            {p.product.reviewFlags.map(reviewText).join(" ")}
            {p.product.importSource?.text
              ? ` În document (pagina ${p.product.importSource.page}): „${p.product.importSource.text}”.`
              : ""}{" "}
            Când salvezi, produsul este marcat ca verificat.
          </Alert>
        )}
        {errorList.length > 0 && (
          <Alert tone="error" live>
            <strong style={{ fontWeight: 600 }}>
              Formularul are {plural(errorList.length, "problemă", "probleme")}:
            </strong>{" "}
            {errorList.join(" ")}
          </Alert>
        )}
        {message && (
          <Alert tone="error" live>
            {message}
          </Alert>
        )}

        <h3 className="ed-h">Date produs</h3>
        <div className="field">
          <label className="field-label" htmlFor="ed-name">
            Nume produs <span className="field-hint">(obligatoriu)</span>
          </label>
          <input
            id="ed-name"
            className="field-input"
            maxLength={150}
            value={form.name}
            data-autofocus=""
            aria-invalid={inv("name")}
            aria-describedby={errors.name ? "ed-name-e" : undefined}
            onChange={(e) => setForm((f) => withSourceChange(f, { name: e.target.value }))}
          />
          {err("ed-name-e", "name")}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="ed-cat">
            Categorie
          </label>
          <select
            id="ed-cat"
            className="field-input"
            value={form.categoryId}
            aria-invalid={inv("categoryId")}
            aria-describedby={errors.categoryId ? "ed-cat-e" : undefined}
            onChange={(e) => set({ categoryId: e.target.value })}
          >
            {(p.askCategory || !form.categoryId) && (
              <option value="" disabled>
                Alege categoria…
              </option>
            )}
            {p.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {err("ed-cat-e", "categoryId")}
        </div>

        <div className="stack" style={{ gap: 10 }}>
          <span id="ed-pt" className="field-label">
            Preț
          </span>
          <fieldset className="segmented" aria-labelledby="ed-pt" style={{ margin: 0 }}>
            <button
              type="button"
              aria-pressed={form.mode === "single"}
              onClick={() => set({ mode: "single" })}
            >
              Preț unic
            </button>
            <button
              type="button"
              aria-pressed={form.mode === "variants"}
              onClick={() => set({ mode: "variants" })}
            >
              Variante / mărimi
            </button>
          </fieldset>
          {form.mode === "single" ? (
            <div className="stack" style={{ gap: 4 }}>
              <div
                className="affix"
                style={{ width: 200, maxWidth: "100%" }}
                data-invalid={inv("pricing.price")}
              >
                <input
                  id="ed-price"
                  inputMode="decimal"
                  aria-label={`Preț (${sym})`}
                  placeholder="ex. 42,00"
                  value={form.price}
                  aria-invalid={inv("pricing.price")}
                  aria-describedby={errors["pricing.price"] ? "ed-price-e" : undefined}
                  onChange={(e) => set({ price: e.target.value })}
                />
                <span>{sym}</span>
              </div>
              {err("ed-price-e", "pricing.price")}
              <span className="caption muted" style={{ fontWeight: 400 }}>
                {pubPriceHint}
              </span>
            </div>
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              <span className="caption muted" style={{ fontWeight: 400 }}>
                Pentru mărimi sau cantități diferite, de exemplu „Mică, 30 cm” 32,00 și „Mare, 40 cm” 45,00.
                Cel puțin două variante. Prețul unic completat rămâne păstrat dacă revii la el.
              </span>
              {form.variants.map((v, i) => (
                <div
                  key={v.key}
                  className="row"
                  style={{ alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}
                >
                  <div style={{ flex: "1 1 140px", display: "flex", flexDirection: "column", gap: 4 }}>
                    <input
                      id={`ed-vl-${v.key}`}
                      className="field-input"
                      aria-label={`Variantă ${i + 1}: etichetă`}
                      placeholder={i === 0 ? "ex. Mică, 30 cm" : "ex. Mare, 40 cm"}
                      maxLength={40}
                      value={v.label}
                      aria-invalid={inv(`pricing.variants.${i}.label`)}
                      onChange={(e) =>
                        set({
                          variants: form.variants.map((x) =>
                            x.key === v.key ? { ...x, label: e.target.value } : x,
                          ),
                        })
                      }
                    />
                    {err(`ed-vl-${v.key}-e`, `pricing.variants.${i}.label`)}
                  </div>
                  <div style={{ width: 140, display: "flex", flexDirection: "column", gap: 4 }}>
                    <div className="affix" data-invalid={inv(`pricing.variants.${i}.price`)}>
                      <input
                        aria-label={`Variantă ${i + 1}: preț în ${currencyWord(p.currency)}`}
                        inputMode="decimal"
                        placeholder="0,00"
                        value={v.price}
                        aria-invalid={inv(`pricing.variants.${i}.price`)}
                        onChange={(e) =>
                          set({
                            variants: form.variants.map((x) =>
                              x.key === v.key ? { ...x, price: e.target.value } : x,
                            ),
                          })
                        }
                      />
                      <span>{sym}</span>
                    </div>
                    {err(`ed-vp-${v.key}-e`, `pricing.variants.${i}.price`)}
                  </div>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Elimină varianta ${i + 1}`}
                    disabled={form.variants.length <= 2}
                    onClick={() => set({ variants: form.variants.filter((x) => x.key !== v.key) })}
                  >
                    <Trash2 {...ICON} aria-hidden="true" />
                  </button>
                </div>
              ))}
              {err("ed-var-e", "pricing.variants")}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ alignSelf: "flex-start" }}
                onClick={() => {
                  const keyId = `n${Date.now()}`;
                  set({ variants: [...form.variants, { key: keyId, label: "", price: "" }] });
                  setTimeout(() => document.getElementById(`ed-vl-${keyId}`)?.focus(), 30);
                }}
              >
                <Plus {...ICON} aria-hidden="true" />
                Adaugă variantă
              </button>
            </div>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="ed-desc">
            Descriere <span className="field-hint">(opțional)</span>
          </label>
          <textarea
            id="ed-desc"
            className="field-input"
            rows={3}
            maxLength={300}
            value={form.description}
            aria-describedby="ed-desc-c"
            onChange={(e) => setForm((f) => withSourceChange(f, { description: e.target.value }))}
          />
          <span id="ed-desc-c" className="caption muted" style={{ alignSelf: "flex-end", fontWeight: 400 }}>
            {form.description.length} / 300
          </span>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="ed-portion">
            Porție / gramaj <span className="field-hint">(opțional)</span>
          </label>
          <input
            id="ed-portion"
            className="field-input"
            style={{ width: 220, maxWidth: "100%" }}
            maxLength={30}
            placeholder="ex. 350 g, 400 ml, 2 buc."
            value={form.portion}
            onChange={(e) => set({ portion: e.target.value })}
          />
        </div>

        {p.product && (
          <div
            className="row"
            style={{
              gap: 12,
              padding: "12px 14px",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
            }}
          >
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <span id="ed-av" className="field-label">
                Disponibil azi
              </span>
              <span className="caption muted" style={{ fontWeight: 400 }}>
                Se aplică imediat în meniul public, separat de salvare.
              </span>
            </div>
            <Switch checked={available} label="Disponibil azi" onChange={toggleAvailability} />
          </div>
        )}

        <h3 className="ed-h">Ingrediente și alergeni</h3>
        <Section
          id="food"
          title="Ingrediente și alergeni"
          sub={foodSub}
          subColor={form.allergensConfirmed ? "var(--color-success)" : "var(--color-warning)"}
          open={open.food}
          onToggle={() => setOpen((o) => ({ ...o, food: !o.food }))}
        >
          <div className="field" style={{ paddingTop: 10 }}>
            <label className="field-label" htmlFor="ed-ingr">
              Ingrediente
            </label>
            <textarea
              id="ed-ingr"
              className="field-input"
              rows={2}
              maxLength={400}
              placeholder="ex. făină de grâu, roșii, mozzarella"
              value={form.ingredients}
              onChange={(e) => setForm((f) => withFoodChange(f, { ingredients: e.target.value }))}
            />
          </div>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>
              Alergeni (lista UE de 14)
            </legend>
            {/* Meniova's estimate (owner 2026-11-03): a suggestion the restaurant checks, never ticked by itself. */}
            {!form.allergensConfirmed && p.product?.allergensEstimated && (
              <div className="estimate-hint">
                <span>
                  <strong>Estimarea Meniova</strong> (din rețeta tipică, văzută acum de clienți):{" "}
                  {p.product.allergensEstimated.length
                    ? p.product.allergensEstimated
                        .map((a) => ALLERGENS.find(([c]) => c === a)?.[1] ?? a)
                        .join(", ")
                    : "niciun alergen din lista UE"}
                  . Verifică rețeta ta: poate fi diferită.
                </span>
                {p.product.allergensEstimated.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      setForm((f) =>
                        withFoodChange(f, {
                          allergens: [...new Set([...f.allergens, ...(p.product?.allergensEstimated ?? [])])],
                        }),
                      )
                    }
                  >
                    Bifează estimarea
                  </button>
                )}
              </div>
            )}
            <div className="check-grid">
              {ALLERGENS.map(([code, label]) => (
                <label key={code} className="checkline">
                  <input
                    type="checkbox"
                    checked={form.allergens.includes(code)}
                    onChange={() =>
                      setForm((f) =>
                        withFoodChange(f, {
                          allergens: f.allergens.includes(code)
                            ? f.allergens.filter((a) => a !== code)
                            : [...f.allergens, code],
                        }),
                      )
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          {/* The recipe and the nutrition values live only in "Valori nutriționale" (owner 2026-11-02). */}
          <p className="caption muted" style={{ margin: 0 }}>
            Rețeta și valorile nutriționale se completează în{" "}
            <Link to="/app/nutrition">Valori nutriționale</Link>.
          </p>
          <label className={`confirm${form.allergensConfirmed ? " ok" : ""}`}>
            <input
              type="checkbox"
              checked={form.allergensConfirmed}
              aria-describedby="ed-alok-h"
              onChange={() => set({ allergensConfirmed: !form.allergensConfirmed })}
            />
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <strong style={{ fontWeight: 600 }}>
                Confirm că ingredientele și alergenii de mai sus sunt corecți și compleți.
              </strong>
              <span id="ed-alok-h" className="muted">
                {form.allergensConfirmed
                  ? form.allergens.length
                    ? "Confirmat. Dacă schimbi lista, confirmarea se resetează."
                    : "Confirmat fără alergeni bifați: clienții vor vedea că ai declarat că produsul nu conține alergeni din lista UE."
                  : "Fără confirmare, clienții văd estimarea Meniova, marcată „neconfirmată de restaurant” (sau „Întreabă personalul”, dacă nu există estimare). Lista ta apare abia după ce confirmi."}
              </span>
            </span>
          </label>
        </Section>

        <div className="frozen-row">
          <div className="stack" style={{ gap: 2, minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>Din produs congelat</span>
            <span className="note" style={{ margin: 0 }}>
              Pornește dacă preparatul (sau un ingredient principal) vine congelat. Clienții văd mențiunea în
              meniu, cum cere legea.
            </span>
          </div>
          <Switch
            checked={form.frozen}
            label="Din produs congelat"
            onChange={() => set({ frozen: !form.frozen })}
          />
        </div>
        <h3 className="ed-h">
          Opțional <span className="field-hint">(nu este necesar pentru publicare)</span>
        </h3>
        <div className="row" style={{ gap: 14, alignItems: "flex-start" }}>
          <div aria-hidden="true" className={`thumb photo-lg${form.imageId ? " has" : ""}`}>
            {form.imageId ? (
              <img src={mediaUrl(p.rid, form.imageId)} alt="" width={96} height={96} />
            ) : (
              <ImageIcon size={22} strokeWidth={1.75} />
            )}
          </div>
          <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 8 }}>
            <span className="field-label" id="ed-photo">
              Fotografie <span className="field-hint">(opțional)</span>
            </span>
            <input
              ref={fileRef}
              type="file"
              accept={PHOTO_ACCEPT}
              tabIndex={-1}
              aria-hidden="true"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) void pickPhoto(file);
              }}
            />
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                aria-describedby="ed-photo"
                disabled={photoBusy}
                onClick={() => fileRef.current?.click()}
              >
                {photoBusy ? "Se încarcă…" : form.imageId ? "Înlocuiește fotografia" : "Încarcă fotografie"}
              </button>
              {form.imageId && !photoBusy && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    set({ imageId: null });
                    setPhotoError("");
                  }}
                >
                  Elimină
                </button>
              )}
            </div>
            <span role="alert" className="field-error">
              {photoError}
            </span>
            <span className="note">
              JPG, PNG sau WebP, până la 8 MB. Fără fotografie, produsul apare doar cu text.
            </span>
          </div>
        </div>

        <Section
          id="labels"
          title="Etichete"
          sub={labelsSub}
          open={open.labels}
          onToggle={() => setOpen((o) => ({ ...o, labels: !o.labels }))}
        >
          <fieldset style={{ border: 0, margin: 0, padding: "10px 0 0" }}>
            <legend className="sr-only">Etichete</legend>
            {PRODUCT_LABELS.map(([code, label]) => (
              <label key={code} className="checkline" style={{ fontSize: 15 }}>
                <input
                  type="checkbox"
                  checked={form.labels.includes(code)}
                  onChange={() =>
                    set({
                      labels: form.labels.includes(code)
                        ? form.labels.filter((l) => l !== code)
                        : [...form.labels, code],
                    })
                  }
                />
                {label}
              </label>
            ))}
            <span className="note">Folosește etichete doar dacă sunt adevărate pentru rețeta actuală.</span>
          </fieldset>
        </Section>

        <Section
          id="tr"
          title="Traducere · English"
          sub={trChip.label}
          subColor={`var(--color-${trChip.tone === "neutral" ? "text-muted" : trChip.tone})`}
          open={open.tr}
          onToggle={() => setOpen((o) => ({ ...o, tr: !o.tr }))}
        >
          <div className="row" style={{ gap: 8, paddingTop: 10 }}>
            <span className="caption muted" style={{ fontWeight: 400 }}>
              Stare:
            </span>
            <StatusChip {...trChip} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="ed-nen">
              Nume · English
            </label>
            <input
              id="ed-nen"
              className="field-input"
              maxLength={150}
              value={form.nameEn}
              onChange={(e) => set({ nameEn: e.target.value, trReviewed: false })}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="ed-den">
              Descriere · English
            </label>
            <textarea
              id="ed-den"
              className="field-input"
              rows={3}
              maxLength={300}
              value={form.descEn}
              onChange={(e) => set({ descEn: e.target.value, trReviewed: false })}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="ed-ien">
              Ingrediente · English
            </label>
            <input
              id="ed-ien"
              className="field-input"
              maxLength={400}
              value={form.ingrEn}
              onChange={(e) => set({ ingrEn: e.target.value })}
            />
          </div>
          <label className="checkline">
            <input
              type="checkbox"
              checked={form.trReviewed}
              onChange={() => set({ trReviewed: !form.trReviewed })}
            />
            Am verificat traducerea
          </label>
          <span className="note">
            Dacă schimbi textul român, traducerea devine „necesită actualizare”. Nu o rescriem automat.
          </span>
        </Section>
      </div>

      {confirmClose && (
        <Modal
          alert
          title="Renunți la modificările nesalvate?"
          body="Ce ai completat în acest formular nu se păstrează. Ciorna și meniul public rămân cum erau."
          onClose={() => setConfirmClose(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                data-autofocus=""
                onClick={() => setConfirmClose(false)}
              >
                Continuă editarea
              </button>
              <button type="button" className="btn btn-danger" onClick={p.onClose}>
                Renunță la modificări
              </button>
            </>
          }
        />
      )}
    </Drawer>
  );
}

function Section({
  id,
  title,
  sub,
  subColor = "var(--color-text-muted)",
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  sub: string;
  subColor?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="acc">
      <button type="button" aria-expanded={open} aria-controls={`ed-sec-${id}`} onClick={onToggle}>
        <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
          <span style={{ fontSize: 13, color: subColor }}>{sub}</span>
        </span>
        <ChevronDown
          {...ICON}
          aria-hidden="true"
          style={{ color: "var(--color-text-muted)", transform: open ? "rotate(180deg)" : undefined }}
        />
      </button>
      {open && (
        <div id={`ed-sec-${id}`} className="acc-body">
          {children}
        </div>
      )}
    </div>
  );
}
